<template>
  <CContainer class="datatable-container pb-2" @click="onContainerClick">
    <Loader :is-processing="isLoading" />
    <CRow class="justify-content-center mt-4">
      <CCol md="12" class="mb-2 mb-sm-4">
        <h1 class="text-center">
          Summary Reports
          <HelpCenterButton classes="ms-2" content-key="summary_reports" />
        </h1>
      </CCol>

      <CCol xs="4" md="6" lg="3">
        <VSelect
          v-model="filterParams.aggregate_by"
          :clearable="false"
          class="cs-select mb-3 ps__child--consume"
          required
          :options="aggregateByOptions"
          placeholder="Choose One"
          :reduce="(item) => item.value"
        />
        <div
          v-if="
            v$.filterParams.aggregate_by.$error &&
            v$.filterParams.aggregate_by.required.$invalid
          "
          class="invalid-feedback"
        >
          This field is required
        </div>
      </CCol>
      <CCol class="d-block d-lg-none" xs="4" md="6" lg="3">
        <VSelect
          v-model="filterParams.by_pass_types"
          class="cs-select auto-dropdown-height mb-3 ps__child--consume"
          :clearable="false"
          :clear-search-on-blur="
            () => {
              return true
            }
          "
          :options="passTypesOptions"
          placeholder="By Pass Type"
          :reduce="(item) => item.value"
        />
      </CCol>
      <CCol xs="4" md="4" lg="3">
        <LazyLoadSelect
          :reports="true"
          :is-multiple="true"
          classes="mb-3"
          :selected="filterParams.teacher_ids"
          type="adults"
          placeholder="Search Teachers"
          @changed="
            (value) => {
              filterParams.teacher_ids = value
            }
          "
        />
      </CCol>
      <CCol xs="4" md="4" lg="3">
        <LazyLoadSelect
          :is-multiple="true"
          classes="mb-3"
          :selected="filterParams.student_ids"
          type="student"
          placeholder="Search Students"
          @changed="
            (value) => {
              filterParams.student_ids = value
            }
          "
        />
      </CCol>
      <CCol xs="4" md="4" lg="3">
        <LazyLoadSelect
          :is-multiple="true"
          classes="mb-3"
          :selected="filterParams.room_ids"
          type="locations"
          placeholder="Search Destinations"
          @changed="
            (value) => {
              filterParams.room_ids = value
            }
          "
        />
      </CCol>

      <CCol class="d-none d-lg-block" xs="4" md="4" lg="3">
        <VSelect
          v-model="filterParams.by_pass_types"
          class="cs-select auto-dropdown-height mb-3 ps__child--consume"
          :clearable="false"
          :clear-search-on-blur="
            () => {
              return true
            }
          "
          :options="passTypesOptions"
          placeholder="By Pass Type"
          :reduce="(item) => item.value"
        />
      </CCol>
      <CCol xs="4" md="4" lg="3">
        <VSelect
          v-if="gradYears"
          v-model="filterParams.by_grad_year"
          class="cs-select mb-3 ps__child--consume"
          multiple
          :clearable="false"
          :clear-search-on-blur="
            () => {
              return true
            }
          "
          :options="gradYears"
          placeholder="By Grad Year"
        />
      </CCol>
      <CCol xs="4" md="4" lg="3">
        <VSelect
          v-if="gradeYears"
          v-model="filterParams.by_grade_years"
          class="cs-select mb-3 ps__child--consume"
          :clearable="false"
          :clear-search-on-blur="
            () => {
              return true
            }
          "
          :options="gradeYears"
          placeholder="By Grad Year"
          @update:model-value="setCalendarDates"
        />
      </CCol>
      <CCol xs="4" md="4" lg="3">
        <DatePicker
          v-if="filterParams.by_grade_years"
          v-model="filterParams.dates"
          class="cs-date-picker"
          :masks="{ input: 'MM/DD/YYYY' }"
          placeholder="Year"
          mode="range"
          is-range
          :min-date="minDate"
          :max-date="maxDate"
        >
          <template #default="{ inputValue, togglePopover, hidePopover }">
            <div class="input-icon-field cursor-pointer">
              <CFormInput
                placeholder="Date"
                type="readonly"
                :value="inputValue.start + '-' + inputValue.end"
                readonly
                class="date-calendar-option cursor-pointer"
                @click="showCalendarRanges(hidePopover, togglePopover)"
              />
              <span
                class="field-icon"
                @click="showCalendarRanges(hidePopover, togglePopover)"
              >
                <i class="fi flaticon-calendar me-1" />
              </span>
              <div
                v-show="calendar.isRangesVisible"
                class="optional-date-ranges-container"
              >
                <div
                  v-for="(range, index) in optionalDateRanges"
                  :key="index"
                  :class="{
                    active: range.label === calendar.activeRange.label
                  }"
                  class="option-range p-2"
                  @click="setCalendarRange(range, togglePopover)"
                >
                  {{ range.label }}
                </div>
              </div>
            </div>
          </template>
        </DatePicker>
      </CCol>
      <CCol md="12" class="mt-1 mt-md-4 text-center text-sm-start">
        <div class="d-inline-block text-center mb-2" @click="advancedFilter()">
          <CButton class="me-2 btn bg-gradient-blue reverse text-white">
            <div class="p-small">Search</div>
          </CButton>
        </div>
        <div class="d-inline-block text-center">
          <CButton
            class="ms-0 ms-sm-2 p-small bg-gradient-blue"
            @click="clearFilter()"
          >
            <span class="btn bg-white px-3"
              ><b class="text-graident-blue px-3">Clear</b></span
            >
          </CButton>
        </div>
        <div class="d-inline-block text-center px-3">
          <CAlert
            v-if="errorMessages && !isLoading"
            class="mt-3"
            color="danger"
          >
            {{ errorMessages }}
          </CAlert>
        </div>
      </CCol>
    </CRow>

    <CRow class="mt-5">
      <CCol md="12">
        <div
          class="table-tabs-header mb-2 bg-white d-flex align-items-center justify-content-between"
        >
          <div
            v-for="(tab, index) in tableTabs"
            :key="index"
            class="table-tab"
            :class="{ active: tab.value === activeTableTab.value }"
          >
            {{ tab.label }}
          </div>
          <div class="actions d-flex align-items-center">
            <CLink
              class="lh-normal text-accent-blue-light ms-2 ms-sm-0"
              download="summary_reports.csv"
              target="_blank"
              @click="handleReports(pagination.activePage, true)"
            >
              <i class="fi flaticon-download me-1" />

              <span class="d-none d-md-inline-block">Download CSV</span>
            </CLink>

            <div
              class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
            >
              <CInputGroup class="w-auto">
                <div class="input-group-prepend">
                  <span class="input-group-text lh-normal"
                    ><div><i class="flaticon-magnifying-glass"></i></div
                  ></span>
                </div>
                <CFormInput
                  placeholder="Search..."
                  v-model="columnSearchKeys.globalQuery"
                  @input="searchByQuery($event.target.value)"
                >
                </CFormInput>
              </CInputGroup>
            </div>
          </div>
        </div>
        <div
          class="d-flex justify-space-between align-items-center status-guides-container mb-2"
        >
          <div
            v-if="filterParams.aggregate_by === 'highest_pass_takers'"
            class="d-flex align-items-center"
          >
            <div class="m3-2">Optional:</div>
            <div class="status-list flex-wrap flex-md-nowrap">
              <CCheckBox
                label="Student Number"
                v-model="showStudentNumber"
                name="Student number"
                :inline="true"
                :custom="true"
                class="custom-checkbox m-0"
              />
            </div>
          </div>
          <per-page-selector :action="setPerPage" :pagination="pagination" />
        </div>

        <DataTable
          v-if="summaryReports"
          class="datatable bg-white text-center"
          :items="summaryReports"
          :fields="activeDataTableFields"
          :column-filter="true"
          :sorter="{ external: true }"
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          @update:sorter-value="sortByColumn"
        >
          <template #student_sis_id="{ item }">
            <td>
              <div>
                {{
                  item.destination.student_sis_id
                    ? item.destination.student_sis_id
                    : ""
                }}
              </div>
            </td>
          </template>
          <template #destination="{ item }">
            <td class="text-start">
              {{ item.destination ? item.destination.name : "" }}
            </td>
          </template>
          <template #destination-filter>
            <input
              v-model="columnSearchKeys.destination.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #total_passes-filter>
            <input
              v-model="columnSearchKeys.total_passes.value"
              placeholder="Search by passes"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #total_time_all="{ item }">
            <td>
              {{ item.total_time_all ? item.total_time_all : "00:00:00" }}
            </td>
          </template>
          <template #total_time_all-filter>
            <input
              v-model="columnSearchKeys.total_time_all.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #total_time_student="{ item }">
            <td>
              {{
                item.total_time_student ? item.total_time_student : "00:00:00"
              }}
            </td>
          </template>
          <template #total_time_student-filter>
            <input
              v-model="columnSearchKeys.total_time_student.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #total_time_aup="{ item }">
            <td>
              {{ item.total_time_aup ? item.total_time_aup : "00:00:00" }}
            </td>
          </template>
          <template #total_time_aup-filter>
            <input
              v-model="columnSearchKeys.total_time_aup.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #total_time_proxy="{ item }">
            <td>
              {{ item.total_time_proxy ? item.total_time_proxy : "00:00:00" }}
            </td>
          </template>
          <template #total_time_proxy-filter>
            <input
              v-model="columnSearchKeys.total_time_proxy.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #total_time_apt="{ item }">
            <td>
              {{ item.total_time_apt ? item.total_time_apt : "00:00:00" }}
            </td>
          </template>
          <template #total_time_apt-filter>
            <input
              v-model="columnSearchKeys.total_time_apt.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #total_time_ksk="{ item }">
            <td>
              {{ item.total_time_ksk ? item.total_time_ksk : "00:00:00" }}
            </td>
          </template>
          <template #total_time_ksk-filter>
            <input
              v-model="columnSearchKeys.total_time_ksk.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>
        </DataTable>

        <Pagination
          v-if="
            pagination &&
            pagination.pages &&
            pagination.pages > 1 &&
            !isLazyLoadingMode
          "
          class="cs-pagination mt-4"
          :dots="false"
          :double-arrows="true"
          :active-page="pagination.activePage"
          :pages="pagination.pages"
          size="lg"
          align="center"
          @update:active-page="setActivePage"
        >
          <template #last-button>
            <i class="flaticon-right-arrow-1" />
          </template>
          <template #first-button>
            <i class="flaticon-left-arrow-2" />
          </template>
          <template #previous-button>
            <i class="fi flaticon-left-arrow-1" />
          </template>
          <template #next-button>
            <i class="fi flaticon-chevron" />
          </template>
        </Pagination>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import Loader from "@/components/Loader"
import LazyLoadSelect from "@/components/LazyLoadSelect.vue"

import { mapGetters } from "vuex"
import moment from "moment-timezone"
import PerPageSelector from "../PerPageSelector.vue"
import useVuelidate from "@vuelidate/core"
import { required, maxLength } from "vuelidate/lib/validators"
import HelpCenterButton from "@/components/HelpCenterButton"

export default {
  name: "SummaryReportsDataTable",
  setup() {
    return { v$: useVuelidate() }
  },
  components: {
    Loader,
    LazyLoadSelect,
    PerPageSelector,
    HelpCenterButton
  },
  data() {
    return {
      errorMessages: null,
      isLoading: false,
      searchTimeOut: null,
      isLazyLoadingMode: false,
      today: moment().format("MMM DD YYYY"),
      gradYears: [],
      filterParams: {
        search_id: "",
        search_query: "",
        sort_query: "",
        aggregate_by: "",
        by_pass_types: "",
        by_grad_year: [],
        student_ids: [],
        teacher_ids: [],
        room_ids: [],
        dates: {
          start: moment().subtract(1, "days")._d,
          end: moment().subtract(1, "days")._d
        },
        by_grade_years: []
      },
      isActiveStudentSearch: false,
      showStudentNumber: false,
      dataTableFields: [
        {
          key: "student_sis_id",
          filter: false,
          sorter: false,
          label: "Student number",
          _style: "min-width:150px"
        },
        {
          key: "destination",
          label: "Destinations",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "total_passes",
          label: "Total Pass",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "total_time_all",
          label: "Total Time",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "total_time_student",
          label: "STU",
          type: "tabular"
        },
        {
          key: "total_time_aup",
          label: "Auto Pass(STU)",
          type: "tabular"
        },
        {
          key: "total_time_proxy",
          label: "TCH(PRX)",
          type: "tabular"
        },
        {
          key: "total_time_apt",
          label: "APT",
          type: "tabular"
        },
        {
          key: "total_time_ksk",
          label: "KSK",
          type: "tabular"
        }
      ],
      aggregateByOptions: [
        {
          value: "highest_pass_destination",
          label: "Locations (pass destination)"
        },
        {
          value: "highest_pass_granters",
          label: "Adults (pass granters)"
        },
        {
          value: "highest_pass_takers",
          label: "Students (pass takers)"
        }
      ],
      passTypesOptions: [
        {
          value: "ALL",
          label: "All Passes"
        },
        {
          value: "STU",
          label: "Student Created Pass (STU)"
        },
        {
          value: "AUP",
          label: "Auto Pass (STU)"
        },
        {
          value: "TCH",
          label: "Teacher Pass (TCH)"
        },
        {
          value: "APT",
          label: "Appointment Pass (APT)"
        },
        {
          value: "KSK",
          label: "Kiosk Pass (KSK)"
        }
      ],
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      tableTabs: [
        {
          label: "Summary Reports",
          value: "summary-reports"
        }
      ],
      activeTableTab: {
        value: "summary-reports"
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      calendar: {
        isCalendarVisible: false,
        isRangesVisible: false,
        activeRange: {
          label: "Yesterday",
          range: {
            start: moment().subtract(1, "days")._d,
            end: moment().subtract(1, "days")._d
          }
        }
      },
      columnSearchKeys: {
        globalQuery: "",
        student_sis_id: {
          value: "",
          column: ["student_sis_id"],
          sortBy: "student_sis_id"
        },
        destination: {
          value: "",
          column: ["destination.sortable_name"],
          sortBy: "destination.sortable_name"
        },
        total_passes: {
          value: "",
          column: ["total_passes"],
          sortBy: "total_passes.raw_int"
        },
        total_time_all: {
          value: "",
          column: ["total_time_all"],
          sortBy: "total_time_all"
        },
        total_time_student: {
          value: "",
          column: ["total_time_student"],
          sortBy: "total_time_student"
        },
        total_time_aup: {
          value: "",
          column: ["total_time_aup"],
          sortBy: "total_time_aup"
        },
        total_time_proxy: {
          value: "",
          column: ["total_time_proxy"],
          sortBy: "total_time_proxy"
        },
        total_time_apt: {
          value: "",
          column: ["total_time_apt"],
          sortBy: "total_time_apt"
        },
        total_time_ksk: {
          value: "",
          column: ["total_time_ksk"],
          sortBy: "total_time_ksk"
        }
      }
    }
  },
  validations: {
    filterParams: {
      aggregate_by: {
        required,
        maxLength: maxLength(40)
      }
    }
  },
  created() {
    this.initDataSet()
    // this.handleReports(1, false, false, true)
    this.getGradYears()
  },
  mounted() {
    this.initLazyLoading()
  },
  unmounted() {
    this.$store.commit("summaryReports/SET_SUMMARY_REPORTS", [])
  },
  computed: {
    ...mapGetters({
      summaryReports: "summaryReports/summaryReports",
      school: "schools/activeSchool"
    }),
    isValid() {
      return !this.v$.filterParams.$invalid
    },
    gradeYears() {
      //if created_at year is missing use 2016 as default start year
      const schoolCreatedAt = this.school.created_at
        ? moment(this.school.created_at).year()
        : 2016

      //if the school is created before July we must show the previous school year also
      const startYear =
        moment(this.school.created_at).month() < 6
          ? schoolCreatedAt - 1
          : schoolCreatedAt
      let gradeYears = []
      const currentYear = new Date().getFullYear()
      const today = moment().format("YYYY-MM-DD HH:mm:ss")
      const newSchoolYear = moment(today).isAfter(
        moment(`${currentYear}-06-30 23:59:59`)
      )
      if (moment(today).year() == currentYear && newSchoolYear) {
        for (let i = 0; i <= currentYear - startYear; i++) {
          gradeYears.push({
            value: [startYear + i, startYear + 1 + i],
            label: `${startYear + i}-${(startYear + 1 + i)
              .toString()
              .slice(-2)}`
          })
        }
      } else {
        for (let i = 0; i < currentYear - startYear; i++) {
          gradeYears.push({
            value: [startYear + i, startYear + 1 + i],
            label: `${startYear + i}-${(startYear + 1 + i)
              .toString()
              .slice(-2)}`
          })
        }
      }

      gradeYears = gradeYears.sort((a, b) => b.value[0] - a.value[0])
      return gradeYears
    },
    maxDate() {
      return new Date(this.filterParams.by_grade_years.value[1], 5, 30)
    },
    minDate() {
      return new Date(this.filterParams.by_grade_years.value[0], 6, 1)
    },
    optionalDateRanges() {
      if (this.filterParams.by_grade_years.value && this.minDate) {
        return [
          {
            label: "Today",
            range: {
              start: moment()._d,
              end: moment()._d
            }
          },
          {
            label: "Yesterday",
            range: {
              start: moment().subtract(1, "days")._d,
              end: moment().subtract(1, "days")._d
            }
          },
          {
            label: "Last 7 days",
            range: {
              start:
                moment().subtract(7, "days") < moment(this.minDate)
                  ? new Date(this.minDate)
                  : moment().subtract(7, "days")._d,
              end: moment()._d
            }
          },
          {
            label: "Last 14 days",
            range: {
              start:
                moment().subtract(14, "days") < moment(this.minDate)
                  ? new Date(this.minDate)
                  : moment().subtract(14, "days")._d,
              end: moment()._d
            }
          },
          {
            label: "Last 30 days",
            range: {
              start:
                moment().subtract(30, "days") < moment(this.minDate)
                  ? new Date(this.minDate)
                  : moment().subtract(30, "days")._d,
              end: moment()._d
            }
          },
          {
            label: "This month",
            range: {
              start: moment().startOf("month")._d,
              end: moment().endOf("month").format("MMM DD YYYY")
            }
          },
          {
            label: "This year",
            range: {
              start:
                moment().startOf("year").add(6, "M").subtract(1, "Y") <
                moment(this.minDate)
                  ? new Date(this.minDate)
                  : moment()
                      .startOf("year")
                      .add(6, "M")
                      .subtract(1, "Y")
                      .format("MMM DD YYYY"),
              end:
                new Date().getFullYear() ==
                this.filterParams.by_grade_years.value[0]
                  ? new Date(this.maxDate)
                  : moment()
                      .startOf("year")
                      .add(5, "M")
                      .add(29, "days")
                      .format("MMM DD YYYY")
            }
          },
          {
            label: "Custom range",
            range: null
          }
        ]
      }
      return []
    },
    activeDataTableFields() {
      return this.dataTableFields.filter((field) =>
        this.showStudentNumber && this.isActiveStudentSearch
          ? field
          : field.key !== "student_sis_id"
      )
    }
  },
  methods: {
    getGradYears() {
      this.$store.dispatch("passBlocks/getGradeYears").then((result) => {
        this.gradYears = result.data.data
      })
    },
    handleReports(
      page,
      isExport,
      isLazyLoadingMode,
      isAdvancedSearch,
      noLoader
    ) {
      this.isLoading = !noLoader
      this.pagination.activePage = page ? page : 1
      const params = {
        per_page: this.pagination.per_page.value,
        page: this.pagination.activePage
      }
      if (this.filterParams.search_query) {
        params.search_query = this.filterParams.search_query
      }
      if (this.filterParams.sort_query) {
        params.sort = this.filterParams.sort_query
      }
      if (this.filterParams.search_id) {
        params.search_id = this.filterParams.search_id
      }
      if (isExport) {
        this.exportSummaryReports(params)
      } else {
        this.getSummaryReports(params, isLazyLoadingMode, isAdvancedSearch)
      }
    },
    getSummaryReports(data, isLazyLoadingMode, isAdvancedSearch) {
      this.isLoading = true

      if (this.isValid) {
        this.isLoading = true
        if (isAdvancedSearch) {
          const params = {
            aggregate_by: this.filterParams.aggregate_by,
            teacher_ids: this.filterParams.teacher_ids.length
              ? this.filterParams.teacher_ids.map((el) => el.value.id)
              : this.filterParams.teacher_ids,
            student_ids: this.filterParams.student_ids.length
              ? this.filterParams.student_ids.map((el) => el.value.id)
              : this.filterParams.student_ids,
            room_ids: this.filterParams.room_ids.length
              ? this.filterParams.room_ids.map((el) => el.value.id)
              : this.filterParams.room_ids,
            by_pass_types: this.filterParams.by_pass_types,
            by_grad_year: this.filterParams.by_grad_year,
            from_date: this.filterParams.dates.start
              ? this.$helpers.currTzDate(this.filterParams.dates.start)
              : null,
            to_date: this.filterParams.dates.end
              ? this.$helpers.currTzDate(this.filterParams.dates.end)
              : null,
            per_page: this.pagination.per_page.value,
            page: this.pagination.activePage
          }
          this.isActiveStudentSearch =
            this.filterParams.aggregate_by === "highest_pass_takers"

          this.isLoading = true
          this.errorMessages = null
          this.$store
            .dispatch("summaryReports/getSummaryReports", params)
            .then((response) => {
              const responseData = response.data.data
              if (responseData && responseData.search_id) {
                this.filterParams.search_id = responseData.search_id
                const params = { ...data, search_id: responseData.search_id }
                this.getSummaryReportsResults(params, isLazyLoadingMode)
              }
              this.isLoading = false
            })
            .catch(() => {
              this.isLoading = false
              this.errorMessages =
                "The results of your search are too large to show, please reduce the dates of your search to see results."
            })
        } else {
          this.getSummaryReportsResults(data, isLazyLoadingMode)
        }
      } else {
        this.isLoading = false
        this.v$.$touch()
      }
    },
    getSummaryReportsResults(data, isLazyLoadingMode) {
      this.errorMessages = null
      this.$store
        .dispatch("summaryReports/getSummaryReportsResults", data)
        .then((response) => {
          const data = response.data
          if (data.data) {
            if (isLazyLoadingMode) {
              this.$store.commit(
                "summaryReports/PUSH_SUMMARY_REPORTS",
                data.data
              )
            } else {
              this.$store.commit(
                "summaryReports/SET_SUMMARY_REPORTS",
                data.data
              )
            }
          }
          if (data.meta) {
            this.pagination.total = data.meta.total
            this.pagination.from = data.meta.from
            this.pagination.to = data.meta.to
            this.pagination.pages = Math.ceil(
              data.meta.total / data.meta.per_page
            )
            this.pagination.activePage = Number(this.pagination.activePage)
            this.pagination = JSON.parse(JSON.stringify(this.pagination))
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
          this.errorMessages =
            "The results of your search are too large to show, please reduce the dates of your search to see results."
        })
    },
    exportSummaryReports(data) {
      const params = {
        aggregate_by: this.filterParams.aggregate_by,
        teacher_ids: this.filterParams.teacher_ids.length
          ? this.filterParams.teacher_ids.map((el) => el.value.id)
          : this.filterParams.teacher_ids,
        student_ids: this.filterParams.student_ids.length
          ? this.filterParams.student_ids.map((el) => el.value.id)
          : this.filterParams.student_ids,
        room_ids: this.filterParams.room_ids.length
          ? this.filterParams.room_ids.map((el) => el.value.id)
          : this.filterParams.room_ids,
        by_pass_types: this.filterParams.by_pass_types,
        by_grad_year: this.filterParams.by_grad_year,
        from_date: this.filterParams.dates.start
          ? this.filterParams.dates.start
          : null,
        to_date: this.filterParams.dates.end
          ? this.filterParams.dates.end
          : null,
        search_id: this.filterParams.search_id,
        page: this.pagination.activePage,
        per_page: [25, 50, 100].includes(this.pagination.per_page.value)
          ? this.pagination.per_page.value
          : "all",
        ...data
      }

      this.isLoading = true
      this.errorMessages = null
      this.$store
        .dispatch("summaryReports/getSummaryReportsResults", {
          export_csv: true,
          student_sis_id: this.showStudentNumber,
          ...params
        })
        .then((response) => {
          if (response.data) {
            this.$download.CSVExport(response.data, "summary_reports")
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
          this.errorMessages =
            "The results of your search are too large to show, please reduce the dates of your search to see results."
        })
    },

    clearFilter() {
      this.resetSearchQuery()
      this.clearFilterParams()
    },
    clearFilterParams() {
      this.filterParams = {
        search_id: this.filterParams.search_id,
        search_query: this.filterParams.search_query,
        sort_query: this.filterParams.sort_query,
        aggregate_by: "",
        teacher_ids: [],
        student_ids: [],
        by_grad_year: [],
        room_ids: [],
        by_pass_types: this.passTypesOptions[0].value,
        by_grade_years: this.gradeYears
          ? this.gradeYears[0]
          : this.filterParams.by_grade_years,
        dates: {
          start: moment().subtract(1, "days")._d,
          end: moment().subtract(1, "days")._d
        }
      }
      this.isActiveStudentSearch =
        this.filterParams.aggregate_by === "highest_pass_takers"
      this.showStudentNumber = false
    },
    setPerPage(option) {
      if (option) {
        this.isLazyLoadingMode = option.label === "All entries"
        this.pagination.per_page = option
        this.handleReports()
      }
    },
    setCalendarDates() {
      this.filterParams.dates = {
        start: this.minDate,
        end: this.maxDate
      }
      this.calendar.activeRange = this.optionalDateRanges[4]
    },
    onContainerClick(e) {
      const target = e.target
      const classList =
        target.classList.contains("date-calendar-option") ||
        target.classList.contains("flaticon-calendar") ||
        target.classList.contains("field-icon")
      if (target && !classList) {
        this.calendar.isRangesVisible = false
      }
    },
    showCalendarRanges(hidePopover, togglePopover) {
      if (this.calendar.isCalendarVisible) {
        hidePopover()
      }
      // let isCurrentRange = (this.filterParams.by_grade_years && this.filterParams.by_grade_years.value[1] === new Date().getFullYear());
      let isCurrentRange = false
      if (this.gradeYears[0] && this.filterParams.by_grade_years) {
        // show dropdown if current school year is selected
        isCurrentRange =
          this.gradeYears[0].label === this.filterParams.by_grade_years.label
      }
      this.calendar.isRangesVisible = isCurrentRange
      this.calendar.isCalendarVisible = !isCurrentRange
      if (this.calendar.isCalendarVisible) {
        togglePopover()
      }
    },
    setCalendarRange(range, togglePopover) {
      const isCustomRange = range && range.label == "Custom range"

      this.calendar.isCalendarVisible = isCustomRange
      this.calendar.isRangesVisible = false // !isCustomRange

      this.calendar.activeRange = range
      this.filterParams.dates = range.range
        ? range.range
        : this.filterParams.dates

      if (this.calendar.isCalendarVisible) {
        togglePopover()
      }
    },
    searchByQuery(value, type) {
      clearInterval(this.searchTimeOut)
      this.searchTimeOut = setTimeout(() => {
        this.handleQuerySearch(value, type)
      }, 500)
    },
    handleQuerySearch(value, type) {
      const query = value.toString()
      if (
        query.replace(/\s/g, "").length > 0 ||
        query.replace(/\s/g, "").length === 0
      ) {
        this.filterParams.search_query = ""
        if (type && type === "column") {
          this.columnSearchKeys.globalQuery = ""
          for (const key in this.columnSearchKeys) {
            if (Object.hasOwnProperty.call(this.columnSearchKeys, key)) {
              const columnData = this.columnSearchKeys[key]
              if (columnData.value) {
                columnData.column.forEach((col) => {
                  this.filterParams.search_query =
                    this.filterParams.search_query +
                    `${col}:"${columnData.value}", `
                })
              }
            }
          }
          this.filterParams.search_query = this.filterParams.search_query.slice(
            0,
            -2
          )
        } else {
          this.resetSearchQuery()
          this.columnSearchKeys.globalQuery = query
          this.filterParams.search_query = query ? '"' + query + '"' : query
        }
        this.handleReports()
      }
    },
    sortByColumn(column) {
      const columnName = this.columnSearchKeys[column.column]
        ? this.columnSearchKeys[column.column].sortBy
        : column.column
      this.filterParams.sort_query = `${columnName}:${
        column.asc ? "asc" : "desc"
      }`
      this.handleReports()
    },
    resetSearchQuery() {
      this.filterParams.search_query = ""
      this.columnSearchKeys = {
        globalQuery: "",
        student_sis_id: {
          value: "",
          column: ["student_sis_id"],
          sortBy: "student_sis_id"
        },
        destination: {
          value: "",
          column: ["destination.sortable_name"],
          sortBy: "destination.sortable_name"
        },
        total_passes: {
          value: "",
          column: ["total_passes"],
          sortBy: "total_passes.raw_int"
        },
        total_time_all: {
          value: "",
          column: ["total_time_all"],
          sortBy: "total_time_all"
        },
        total_time_student: {
          value: "",
          column: ["total_time_student"],
          sortBy: "total_time_student"
        },
        total_time_aup: {
          value: "",
          column: ["total_time_aup"],
          sortBy: "total_time_aup"
        },
        total_time_proxy: {
          value: "",
          column: ["total_time_proxy"],
          sortBy: "total_time_proxy"
        },
        total_time_apt: {
          value: "",
          column: ["total_time_apt"],
          sortBy: "total_time_apt"
        },
        total_time_ksk: {
          value: "",
          column: ["total_time_ksk"],
          sortBy: "total_time_ksk"
        }
      }
    },
    initLazyLoading() {
      const body = document.getElementsByClassName("main-scroll-container")[0]
      if (body) {
        body.onscroll = () => {
          if (
            this.isLazyLoadingMode &&
            this.summaryReports &&
            this.summaryReports.length &&
            this.pagination.total > this.summaryReports.length
          ) {
            if (body.offsetHeight + body.scrollTop + 1 >= body.scrollHeight) {
              this.pagination.activePage = this.pagination.activePage + 1
              this.handleReports(
                this.pagination.activePage,
                false,
                true,
                false,
                false
              )
            }
          }
        }
      }
    },
    initDataSet() {
      this.filterParams.by_grade_years = this.gradeYears
        ? this.gradeYears[0]
        : this.filterParams.by_grade_years
      this.filterParams.by_pass_types = this.passTypesOptions[0].value
    },
    setActivePage(page) {
      this.pagination.activePage = page
      this.handleReports(this.pagination.activePage)
      this.$helpers.scrollToTop()
    },
    advancedFilter() {
      this.filterParams.sort_query = ""
      this.resetSearchQuery()
      this.handleReports(1, false, false, true)
    }
  }
}
</script>
